import { useEffect, useState } from 'react';
import { firebase } from '../../../../services/firebase';
import { doc, getDoc, collection, setDoc } from 'firebase/firestore';
import { logError } from '../../../../utils/analytics';
import { useInterval } from 'primereact/hooks';
import { v4 as uuidv4 } from 'uuid';

export const useChatJourney = (userId, guestId, isOwner, dispatch, state, shopDetails, senderName) => {
    const [shouldRedirect, setShouldRedirect] = useState(false);

    useInterval(() => {
        if (shouldRedirect) {
            dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: false });
            setShouldRedirect(false);
        }
    }, shouldRedirect ? 2000 : null);

    useEffect(() => {
        const loadChatJourney = async () => {
            if (!userId || isOwner) return;

            try {
                const journeyRef = doc(firebase, `users/${userId}/chatJourneys/default`);
                const journeyDoc = await getDoc(journeyRef);
                if (journeyDoc.exists()) {
                    const nodes = journeyDoc.data().nodes;

                    dispatch({ type: 'SET_CHAT_JOURNEY', payload: nodes });
                }
            } catch (error) {
                logError('loadChatJourney', error);
            }
        };

        loadChatJourney();
    }, [userId, isOwner, dispatch]);

    useEffect(() => {
        const checkShowChatJourney = async () => {
            if (!userId || !guestId || isOwner) {
                dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: false });
                return;
            }

            try {
                const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
                const chatDoc = await getDoc(chatRef);

                if (!chatDoc.exists()) {
                    const journeyRef = doc(firebase, `users/${userId}/chatJourneys/default`);
                    const journeyDoc = await getDoc(journeyRef);

                    if (journeyDoc.exists() && journeyDoc.data().nodes?.length > 0) {
                        const isEnabled = journeyDoc.data().isEnabled;
                        const firstNode = journeyDoc.data().nodes[0];

                        dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: isEnabled });
                        isEnabled &&
                            dispatch({
                                type: 'SET_MESSAGES', payload: [
                                    { type: 'user', text: firstNode.data.question, sender: shopDetails?.shopName }
                                ]
                            });
                        // Create a new node without the response field
                        const { response, ...dataWithoutResponse } = firstNode.data;
                        const rootNode = {
                            ...firstNode,
                            data: dataWithoutResponse
                        };
                        dispatch({ type: 'SET_CURRENT_JOURNEY_NODE', payload: rootNode });
                        dispatch({ type: 'SET_CHAT_JOURNEY', payload: journeyDoc.data().nodes });
                    } else {
                        dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: false });
                    }
                } else {
                    // If chat exists, don't show journey
                    dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: false });
                }
            } catch (error) {
                logError('checkShowChatJourney', error);
                dispatch({ type: 'SET_SHOW_CHAT_JOURNEY', payload: false });
            }
        };

        checkShowChatJourney();
    }, [userId, guestId, isOwner, dispatch, shopDetails]);

    const handleJourneyChoice = (childNode) => {
        if (!childNode) return;

        // Validate and truncate user message if needed
        const userMessage = childNode.data.question;

        // Add user's choice to messages
        const updatedMessages = [...state.messages, {
            type: 'guest',
            text: userMessage,
            sender: senderName
        }];

        // Update journey state
        dispatch({ type: 'SET_PARENT_JOURNEY_NODE', payload: state.currentJourneyNode });
        dispatch({ type: 'SET_CURRENT_JOURNEY_NODE', payload: childNode });

        // First dispatch the user's choice
        dispatch({ type: 'SET_MESSAGES', payload: updatedMessages });

        // If this is a leaf node (no children), show the response and end the journey
        if (!childNode.children || childNode.children.length === 0) {
            // Only add response if it's not empty
            if (childNode.data.response) {
                // Validate and truncate bot response if needed
                const botResponse = childNode.data.response;

                setTimeout(() => {
                    dispatch({
                        type: 'SET_MESSAGES',
                        payload: [...updatedMessages, {
                            type: 'guest',
                            text: botResponse,
                            sender: shopDetails?.shopName
                        }]
                    });
                }, 400);
            }
        } else {
            // If there are more options, show the response
            if (childNode.data.response) {
                // Validate and truncate bot response if needed
                const botResponse = childNode.data.response;

                setTimeout(() => {
                    dispatch({
                        type: 'SET_MESSAGES',
                        payload: [...updatedMessages, {
                            type: 'guest',
                            text: botResponse,
                            sender: shopDetails?.shopName
                        }]
                    });
                }, 400);
            }
        }
    };

    const handleJourneyBack = (chatJourney) => {
        if (!state.parentJourneyNode) return;

        const findParentOfNode = (nodes, targetKey) => {
            for (const node of nodes) {
                if (node.key === targetKey) return null;
                if (node.children) {
                    for (const child of node.children) {
                        if (child.key === targetKey) return node;
                        const found = findParentOfNode([child], targetKey);
                        if (found) return found;
                    }
                }
            }
            return null;
        };

        const nodesArray = Array.isArray(chatJourney) ? chatJourney : (chatJourney ? [chatJourney] : []);
    const grandParentNode = findParentOfNode(nodesArray, state.parentJourneyNode.key);

        dispatch({
            type: 'SET_MESSAGES', payload: [
                ...state.messages,
                { type: 'guest', text: '⬅️ Back to previous', sender: senderName },
                { type: 'user', text: state.parentJourneyNode.data.response, sender: shopDetails?.shopName }
            ]
        });
        dispatch({ type: 'SET_CURRENT_JOURNEY_NODE', payload: state.parentJourneyNode });
        dispatch({ type: 'SET_PARENT_JOURNEY_NODE', payload: grandParentNode });
    };

    // Helper to build journey path as readable string
    function buildJourneyFlow() {
        // Traverse up and collect nodes
        let flow = [];
        let node = state.currentJourneyNode;
        let parent = state.parentJourneyNode;
        let nodes = [];
        while (parent) {
            nodes.unshift(parent);
            parent = parent.parent;
        }
        if (node) nodes.push(node);
        // Build flow as array of {role, text}
        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (!n || !n.data) continue;
            // If this is the root node (first node), treat as bot question
            if (i === 0 && n.data.question) {
                flow.push({ role: 'bot', text: n.data.question });
            }
            // For all other nodes, treat question as client action (choice)
            else if (n.data.question) {
                flow.push({ role: 'client', text: n.data.question });
            }
            // Bot response (for leaf nodes or after choice)
            if (n.data.response && (!n.children || n.children.length === 0)) {
                flow.push({ role: 'bot', text: n.data.response });
            }
        }
        // Add the final action: talk to representative
        flow.push({ role: 'client', text: 'talk to representative' });
        return flow;
    }

    const handleTalkToShopOwner = async () => {
        const journeyPath = buildJourneyFlow();
        // Send journey path as a sequence of messages to Firestore
        if (journeyPath && userId && guestId && typeof senderName !== 'undefined') {
            try {
                const messagesCollection = collection(firebase, `users/${userId}/chats/${guestId}/messages`);
                for (const step of journeyPath) {
                    const messageId = uuidv4();
                    let senderLabel = '';
                    if (step.role === 'bot') {
                        senderLabel = shopDetails?.shopName || 'Bot';
                    } else {
                        senderLabel = senderName || 'Client';
                    }
                    const messageData = {
                        id: messageId,
                        text: step.text,
                        sender: senderLabel,
                        timestamp: new Date(),
                        type: 'system',
                        meta: { journeyPath: true, systemName: senderLabel, systemRole: step.role }
                    };
                    await setDoc(doc(messagesCollection, messageId), messageData);
                }
                // Update lastMessageTimestamp to show in shop's chatlist
                const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
                await setDoc(chatRef, { lastMessageTimestamp: new Date() }, { merge: true });
            } catch (err) {
                logError('sendJourneyPathToShop', err);
            }
        }
        // Do NOT add the journey path to local state (guest should not see it)
        dispatch({
            type: 'SET_MESSAGES', payload: [
                ...state.messages,
                { type: 'guest', text: '💬 Talk to shop representative', sender: senderName },
                { type: 'user', text: 'Sure! Connecting you to our shop representative now...', sender: shopDetails?.shopName }
            ]
        });
        setShouldRedirect(true);
    };


    return {
        handleJourneyChoice,
        handleJourneyBack,
        handleTalkToShopOwner
    };
}; 